import { useMountEffect } from '@react-hookz/web';
import SvgChevronDown from '@unifyapps/icons/outline/ChevronDown';
import SvgChevronUp from '@unifyapps/icons/outline/ChevronUp';
import { Box } from '@unifyapps/ui/components/Box';
import { Link } from '@unifyapps/ui/components/Link';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import { DATA_NAV_ITEM_ATTRIBUTE } from '../../../constants';
import type { NavApplicationItemType, NavItem, NavItemGroup } from '../../../types';
import { resolveOpenInNewTab } from '../../../utils/redirection';
import {
  navGroupChevronVariants,
  navGroupItemContentVariants,
  navGroupSubItemVariants,
  navItemLabelVariants,
} from '../styles';
import { Icon } from './Icon';

type Props = {
  navItem: NavItemGroup;
  activeNavItem: NavItem | NavApplicationItemType | undefined;
  isActive: boolean;
  className?: string;
  openGroupId?: string | null;
  setOpenGroupId?: Dispatch<SetStateAction<string | null>>;
};

type SubItemProps = {
  item: NavItem;
  active: boolean;
  readonly?: boolean;
};

function GroupSubItem({ item, active, readonly }: SubItemProps) {
  const { name, url, icon, activeIcon } = item;
  const openInNewTab = resolveOpenInNewTab(item);
  const redirectionProps = useMemo(
    () => (openInNewTab ? { target: '_blank' as const } : {}),
    [openInNewTab],
  );

  const navItemContentClassName = navGroupSubItemVariants({
    active,
  });

  const navItemLabelClassName = navItemLabelVariants({
    active,
  });

  return (
    <Link
      href={readonly ? '#' : url}
      key={item.id}
      underline="none"
      {...redirectionProps}
      {...{ [DATA_NAV_ITEM_ATTRIBUTE]: item.id }}
      className="h-10 w-full items-center rounded-sm"
    >
      <Box className={navItemContentClassName}>
        <Icon active={active} activeIcon={activeIcon} icon={icon} />
        <Typography className={navItemLabelClassName} weight="medium">
          {name}
        </Typography>
      </Box>
    </Link>
  );
}

export function GroupItem({
  navItem,
  activeNavItem,
  isActive: active,
  className,
  openGroupId,
  setOpenGroupId,
}: Props) {
  const { name, items: subItems, icon, activeIcon } = navItem;
  const isExpanded = navItem.id === openGroupId;

  const navItemGroupClassName = navGroupItemContentVariants({
    active,
  });

  const navItemLabelClassName = navItemLabelVariants({
    active,
  });

  const navGroupChevronClassName = navGroupChevronVariants({
    active,
  });

  const onClick = useCallback(() => {
    setOpenGroupId?.(isExpanded ? null : navItem.id);
  }, [isExpanded, navItem.id, setOpenGroupId]);

  useMountEffect(() => {
    if (active) {
      if (openGroupId !== navItem.id) {
        setOpenGroupId?.(navItem.id);
      }
    }
  });

  return (
    <Stack alignItems="center" className={clsx('gap-y-xs', className)}>
      <Stack
        className={navItemGroupClassName}
        direction="row"
        justifyContent="space-between"
        onClick={onClick}
      >
        <Box className="gap-x-md flex items-center">
          <Icon active={active} activeIcon={activeIcon} icon={icon} />
          <Typography className={navItemLabelClassName} weight="medium">
            {name}
          </Typography>
        </Box>
        <Box>
          {isExpanded ? (
            <SvgChevronUp className={navGroupChevronClassName} strokeWidth={2} />
          ) : (
            <SvgChevronDown className={navGroupChevronClassName} />
          )}
        </Box>
      </Stack>
      {isExpanded ? (
        <Stack className="gap-y-xs w-full ps-6" direction="column">
          {/* safe to assume that the items are all URL items for now
         since we do not have support for 2-level or more nested Nav items
        */}
          {subItems.map((subItem: NavItem) => {
            const isSubItemActive = subItem.id === activeNavItem?.id;

            return (
              <GroupSubItem
                active={isSubItemActive}
                item={subItem}
                key={subItem.id}
                readonly={subItem.readonly}
              />
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}
